import ReceiptIcon from '@mui/icons-material/Receipt';
import { Alert, AlertTitle, Button, ButtonGroup } from '@mui/material';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import {
  IsTenantAdministrator,
  useIsTenantAdministrator
} from 'shared/permissions/IsTenantAdministrator';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import TenantPage from 'layouts/TenantPage';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { medicalServiceService } from 'shared/services';
import toast from 'shared/utils/toast';
import { useMedicalServiceMetadata } from '../hooks/useMedicalServiceMetadata';
import { baseCell } from 'components/cells/BaseCell';
import { AddButton } from 'components/ui';
import { MedicalServicesColumns } from '../medical-services';

const PREFIX = 'pages.medical-services.list';

export const MedicalServices = () => {
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const isAdmin: boolean = useIsTenantAdministrator();

  const [openLisDialog, setOpenLisDialog] = useState(false);
  const [openImportLisDialog, setOpenImportLisDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const { etlHeaderNames, extraFields, checkMissingTranscodes } = useMedicalServiceMetadata();

  const missingFields = checkMissingTranscodes();
  const hasMissingFields = missingFields.length > 0;

  const columns = MedicalServicesColumns(PREFIX, etlHeaderNames, isAdmin);

  if (extraFields.length > 0) {
    extraFields.forEach((f) => {
      columns.push({
        field: `${f.field_to}`,
        headerName: f.transcode ? f.transcode : f.field_to_description,
        flex: 1,
        renderCell: baseCell
      });
    });
  }

  const onOpenLisDialog = () => setOpenLisDialog(!openLisDialog);
  const onUploadSuccess = () => toast.success('Importazione avviata.');
  const handleImportLisClick = () => {
    if (hasMissingFields) {
      setShowAlert(true);
      toast.error('Impossibile procedere: transcodifiche mancanti.');
      return;
    }
    setShowAlert(false);
    setOpenImportLisDialog(true);
  };

  const handleClose = () => setOpenImportLisDialog(false);
  const handleLisClose = () => setOpenLisDialog(false);

  return (
    <TenantPage
      title={t(`${PREFIX}.title`)}
      subtitle={t(`${PREFIX}.subtitle`)}
      menuRight={
        <ButtonGroup>
          <IsTenantAdministrator>
            <Button onClick={handleImportLisClick} startIcon={<ReceiptIcon />}>
              {t(`${PREFIX}.import-lis`)}
            </Button>
            <Button onClick={onOpenLisDialog} startIcon={<ReceiptIcon />}>
              {t(`${PREFIX}.add-lis`)}
            </Button>
            <AddButton prefix={PREFIX} to="new" />
          </IsTenantAdministrator>
        </ButtonGroup>
      }>
      {showAlert && hasMissingFields && (
        <Alert severity="warning" sx={{ marginBottom: 3, alignItems: 'center' }}>
          <AlertTitle sx={{ fontWeight: 'bold' }}>
            Importazione Standard LIS Bloccata - Transcodifiche Mancanti
          </AlertTitle>
          Per poter procedere con l&apos;importazione LIS, è necessario mappare i seguenti campi
          obbligatori nella sezione di configurazione ETL: {missingFields.join(', ')}
        </Alert>
      )}

      <EnhancedDataGrid
        refresh={datagridRefresh}
        service={medicalServiceService}
        columns={columns}
      />

      <ImportFileUploader
        open={openLisDialog}
        onClose={handleLisClose}
        hideTriggerButton
        service={medicalServiceService}
        onUploadSuccess={onUploadSuccess}
        importMethod={medicalServiceService.importLisFile}
        infoBox={false}
      />

      <ImportFileUploader
        open={openImportLisDialog}
        onClose={handleClose}
        hideTriggerButton
        service={medicalServiceService}
        onUploadSuccess={onUploadSuccess}
        importMethod={medicalServiceService.importAggregatedLinesFromFile}
        subtitle={t(`${PREFIX}.subtitle`)}
      />
    </TenantPage>
  );
};

export default MedicalServices;
